/* eslint-disable max-len */
import type { TefComsCustomerAttribute } from '@mfe-4cs/components/tef-coms-customer-attribute';
import { CustomerAttribute, CustomerAttributeGroupItem } from '@mfec/customerAttributes/types';
import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { templateContent } from 'lit/directives/template-content.js';
import { classMap } from 'lit/directives/class-map.js';
import { TefComsCustomerAttributeBase } from '@mfec/customerAttributes/TefComsCustomerAttributeBase';
import comsStore from '@mfe-4cs/state/ComsStore';
import { action } from 'mobx';
import { localDate, localTime } from '../util';

export function getCustomerAttributesWithBulletpointTemplateResult(customerAttribute: CustomerAttribute, type: 'standard' | 'extra') {
  return customerAttribute[type].items.map((item: CustomerAttributeGroupItem) => html`
    <p class="coms-consent" style="display: flex">●&nbsp;&nbsp;${unsafeHTML(item.title)}</p>
  `);
}

export function getCustomerAttributesWithIconTemplateResult(customerAttribute: CustomerAttribute) {
  // Here granted=undefined should NOT be displayed
  return customerAttribute.extra.items.filter((item) => item.display).map((item: CustomerAttributeGroupItem) => html`
    <li><tef-icon icon="${item.granted ? 'check' : 'cross'}"></tef-icon>${unsafeHTML(item.title)}</li>`);
}

export function getCustomerAttributesWithCheckboxTemplateResult(customerAttribute: CustomerAttribute) {
  const brandSuffix = /\/\d+$/; // z.B: /1 for o2 or /2 for blau
  const isBrand = brandSuffix.test(comsStore.config?.occlUri as string);
  const classes = { 'form-group-checkbox': true, 'form-group-checkbox--size-small': isBrand };

  return customerAttribute.extra.items.map((item: CustomerAttributeGroupItem) => html`
    <p>
      <span class=${classMap(classes)}>
        <input id=${item.id} type="checkbox" .checked=${!!item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}"></input>
        <label for=${item.id}>${unsafeHTML(item.title)}</label>
      </span>
    </p>
  `);
}

export function getExtraCustomerAttributeItemsTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  switch (itemsDisplayStyle) {
    case 'bulletpoint':
      return html`
        ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'extra')}`;
    case 'icon':
      return html`<ul class="list--icon">${getCustomerAttributesWithIconTemplateResult(customerAttribute)}</ul>`;
    case 'checkbox':
      return html`
        <div class="form-row form-row--group">${getCustomerAttributesWithCheckboxTemplateResult(customerAttribute)}</div>`;
    default:
      return null;
  }
}

export function getLastChangeTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  if (itemsDisplayStyle !== 'bulletpoint') {
    return html`
      <p>
        <small>
          <tef-coms-icon icon="clock"></tef-coms-icon>
          Letzte Änderung am ${localDate(customerAttribute.date)} um ${localTime(customerAttribute.date)} Uhr
        </small>
      </p>
    `;
  }
  return null;
}

export function getButtonsTemplateResult(comp: TefComsCustomerAttributeBase, itemsDisplayStyle: string) {
  if (itemsDisplayStyle === 'checkbox') {
    return html`
      <div class="section-buttons action-primary" slot="action-primary">
        <button class="button button-default" data-modal-close @click=${action(() => { comp.save(); })}>Weiter</button>
      </div>
      <div class="section-buttons action-secondary" slot="action-secondary">
        <button class="button button-border" data-modal-close @click=${() => { comp.cancel(); }}>Abbrechen</button>
      </div>
    `;
  }
  return html`
   <div class="section-buttons action-primary" slot="action-secondary">
      <button class="button button-default" data-modal-close>Zurück</button>
    </div>
  `;
}

export const tefComsCustomerAttributeModalDialog = function render(
  this: TefComsCustomerAttributeBase,
  customerAttribute: CustomerAttribute,
  itemsDisplayStyle: string,
  template$: HTMLTemplateElement,
) {
  return html`
    <tef-modal id="${this.modalId}" cloak>
      <div slot="title"></div>
      <div slot="body">
        <div class="typography-display--6">${templateContent(template$)}</div>
        <p><em>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</em></p>
        ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}
        <p><em>${unsafeHTML(customerAttribute.extra.title || 'Zusatzprodukte')}</em></p>
        ${getExtraCustomerAttributeItemsTemplateResult(customerAttribute, itemsDisplayStyle)}
        ${getLastChangeTemplateResult(customerAttribute, itemsDisplayStyle)}
        <p>${unsafeHTML(customerAttribute.footer)}</p>
      </div>
      ${getButtonsTemplateResult(this, itemsDisplayStyle)}
    </tef-modal>
  `;
};

export const tefComsCustomerAttributeModalTrigger = function render(
  this: TefComsCustomerAttribute,
  template$: HTMLTemplateElement,
) {
  return html`
    <tef-modal-trigger modal-id="tef-coms-customer-attribute-${this.id}">
      <a href='#'>${templateContent(template$)}</a>
    </tef-modal-trigger>`;
};
